import React, { useState, useEffect, useCallback } from "react";
import { colors } from "../shared/constants";
import { DateStrip } from "./DateStrip";

// The same day-strip-and-rows shape as the Calendar tab, sized for the drawer.
// A native <select> was the wrong control here: a day at 10-minute intervals is
// ~50 options, and the old one loaded every slot in the campaign into one list.
export function SlotPicker({
  client,
  campaignId,
  value,
  initialDate,
  onChange,
  currentBookingId,
}) {
  const [days, setDays] = useState([]);
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(
    (forDate) => {
      if (!campaignId) return;
      setLoading(true);
      setError("");
      const q = forDate ? `?date=${forDate}` : "";
      client
        .get(`/campaigns/${campaignId}/schedule/${q}`)
        .then((r) => {
          setDays(r.data?.days || []);
          setDate(r.data?.date || null);
          setSlots(r.data?.slots || []);
          setLoading(false);
        })
        .catch(() => {
          setError("Could not load the available times.");
          setLoading(false);
        });
    },
    [client, campaignId]
  );

  // Open on the day the booking's slot is already on — the caller knows it from
  // the board row, so there is nothing to go looking for. After mount the strip
  // drives which day is shown.
  useEffect(() => {
    load(initialDate);
  }, [load]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <Hint>Loading times...</Hint>;
  if (error) return <Hint tone={colors.red}>{error}</Hint>;
  if (!days.length)
    return <Hint>This campaign has no inspection times set up.</Hint>;

  return (
    <div>
      <DateStrip days={days} active={date} onSelect={load} compact />

      <div
        style={{
          maxHeight: "240px",
          overflowY: "auto",
          border: `1px solid ${colors.border}`,
          borderRadius: "8px",
        }}
      >
        {slots.length === 0 && <Hint>No times on this day.</Hint>}
        {slots.map((s) => {
          // A slot held by the booking being edited is this booking's own time,
          // not someone else's — it reads as selected, never as taken.
          const mine = s.assigned.some(
            (a) => currentBookingId && a.booking_id === currentBookingId
          );
          const others = s.assigned.filter(
            (a) => !currentBookingId || a.booking_id !== currentBookingId
          );
          const selected = String(s.id) === String(value);
          const full = others.length >= s.capacity || s.is_blocked;
          const selectable = !full || mine;

          return (
            <SlotOption
              key={s.id}
              slot={s}
              others={others}
              selected={selected}
              selectable={selectable}
              onPick={() => onChange(selected ? "" : String(s.id))}
            />
          );
        })}
      </div>

      {value ? (
        <button
          onClick={() => onChange("")}
          style={{
            background: "none",
            border: "none",
            color: colors.slate,
            cursor: "pointer",
            fontSize: "12px",
            padding: "8px 0 0",
          }}
        >
          Clear the selected time
        </button>
      ) : (
        <div style={{ fontSize: "12px", color: colors.slate, paddingTop: "8px" }}>
          Pick a time, or leave it unset to book the day without one.
        </div>
      )}
    </div>
  );
}

function Hint({ children, tone }) {
  return (
    <div style={{ fontSize: "13px", color: tone || colors.slate, padding: "10px 2px" }}>
      {children}
    </div>
  );
}

function SlotOption({ slot, others, selected, selectable, onPick }) {
  const [hovered, setHovered] = useState(false);
  const taken = others[0];

  let right = "Available";
  let rightColor = colors.slate;
  if (selected) {
    right = "Selected";
    rightColor = colors.white;
  } else if (slot.is_blocked) {
    right = "Blocked";
  } else if (taken) {
    right = `${taken.unit_number}${taken.unit_name ? ` · ${taken.unit_name}` : ""}`;
    rightColor = colors.slate;
  } else if (slot.capacity > 1) {
    right = `${slot.spaces_left} free`;
  }

  return (
    <div
      onClick={() => selectable && onPick()}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "grid",
        gridTemplateColumns: "110px 1fr",
        gap: "10px",
        alignItems: "center",
        padding: "9px 12px",
        borderBottom: `1px solid ${colors.border}`,
        fontSize: "13px",
        cursor: selectable ? "pointer" : "default",
        opacity: selectable ? 1 : 0.5,
        backgroundColor: selected
          ? colors.darkGrey
          : hovered && selectable
          ? "#f8fafc"
          : colors.white,
        color: selected ? colors.white : colors.text,
      }}
    >
      <div style={{ fontWeight: selected ? "700" : "600" }}>{slot.label}</div>
      <div
        style={{
          color: rightColor,
          fontStyle: !selected && !taken && !slot.is_blocked ? "italic" : "normal",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {right}
      </div>
    </div>
  );
}
